import type { ResponsibleEntry } from "@/mocks/protocolResponses";

interface Props {
  responsables: ResponsibleEntry[];
}

const momentoColorMap: Record<string, string> = {
  Inicio: "bg-blue-100 text-blue-800",
  Desarrollo: "bg-secondary-100 text-secondary-800",
  Cierre: "bg-accent-100 text-accent-800",
  Seguimiento: "bg-purple-100 text-purple-800",
};

function getMomentoStyle(momento: string): string {
  if (momento.includes("Inicio")) return momentoColorMap["Inicio"];
  if (momento.includes("Desarrollo")) return momentoColorMap["Desarrollo"];
  if (momento.includes("Cierre")) return momentoColorMap["Cierre"];
  if (momento.includes("Seguimiento")) return momentoColorMap["Seguimiento"];
  return "bg-background-200 text-foreground-600";
}

export default function ProtocolResponsibleTable({ responsables }: Props) {
  if (responsables.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
          <i className="ri-team-line text-primary-600 text-sm" />
        </div>
        <h3 className="text-base font-semibold text-foreground-900">Responsables Involucrados</h3>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-background-200/80 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200 bg-background-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                <i className="ri-user-star-line mr-1.5" />
                Responsable
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                <i className="ri-task-line mr-1.5" />
                Acción
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wide whitespace-nowrap">
                <i className="ri-timer-line mr-1.5" />
                Momento
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                <i className="ri-book-open-line mr-1.5" />
                Fuente
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-100">
            {responsables.map((resp, idx) => (
              <tr key={idx} className="hover:bg-background-50/50 transition-colors">
                <td className="px-4 py-3">
                  <span className="text-sm font-semibold text-foreground-900">{resp.rol}</span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm text-foreground-700 leading-relaxed max-w-md">{resp.accion}</p>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex text-xs font-medium px-2.5 py-1 rounded-full ${getMomentoStyle(resp.momento)}`}>
                    {resp.momento}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-primary-600">{resp.fuente}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {responsables.map((resp, idx) => (
          <div key={idx} className="bg-white border border-background-200/80 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-foreground-900">{resp.rol}</h4>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getMomentoStyle(resp.momento)}`}>
                {resp.momento}
              </span>
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed mb-2">{resp.accion}</p>
            <p className="text-xs text-primary-600">
              <i className="ri-book-open-line mr-1" />
              {resp.fuente}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}